import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleSnackBar, toggleModal, updateMessages } from 'apps/auth/actions/appAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DocsList } from 'apps/auth/components/common/components/DocsList/DocsList';
import { DocsListItem } from 'apps/auth/components/common/components/DocsList/DocsListItem';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DetailsItem } from 'apps/auth/components/common/DetailsItem/DetailsItem';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import PaymentStatus from 'apps/auth/components/status/PaymentStatus';
import BasePage from 'apps/auth/components/utils/BasePage';
import Loading from 'apps/auth/components/utils/Loading';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { NOT_NOW_CLICKED, BANK_ACCOUNT_I_PAID_CLICKED, BANK_ACCOUNT_PAY_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { CATEGORY_ACCOUNT_DEBIT_VIA_MODULR, DELIVERY_BANK_ACCOUNT } from 'apps/auth/constants/Constants';
import { AUTH } from 'apps/auth/constants/UrlConstants';
import { fetchData } from 'apps/auth/helpers/api';
import { logInfo } from 'apps/auth/helpers/clientLogger';
import { getIbanWithWhitespace } from 'apps/auth/helpers/historyUtils';
import { copyTextToClipboard, getFormattedCardOrAccountNumber, getReference, getSortCodeWithHyphens } from 'apps/auth/helpers/utils';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import PayNotNowPopup from './popup/PayNotNowPopup';

const BankTransferInfo = (props) => {
    const {
        titleNotNow,
        bankTransferInfoBankCode,
        copyBtn,
        copied,
        bankTransferSortCode,
        shareDetails,
        bankTransferAccountNumber,
        bankTransferInfoNextButton,
        bankTransferInfoRecipientBranch,
        bankTransferReferenceAdditionalInfo,
        bankTransferReferenceInfo,
        bankTransferInfoRecipient,
        bankTransferInfoRecipientBank,
        bankTransferInfoIban,
        subTitleNotNow,
        titleIPaid,
        subTitleIPaid,
        gotIt,
        bankTransferInfoAmountToSend,
        bankTransferInfoAmountToSendInfo,
        bankTransferInfoRecipientName,
        bankTransferInfoRecipientAddress,
        bankTransferInfoRecipientCity,
        bankTransferInfoRecipientState,
        bankTransferInfoRecipientPostalCode,
        bankTransferInfoCancelButton,
        textShareDescription,
        bankTransferTitle,
        guideTitleLink,
        titleDisclaimer,
        paymentGuideStep1Title,
        paymentGuideStep1SubTitle,
        paymentGuideStep2Title,
        paymentGuideStep2SubTitle,
        paymentGuideStep3Title,
        paymentGuideStep3SubTitle,
        paymentGuideStep4Title,
        paymentGuideStep4SubTitle,
    } = usePageMessages();
    const { shareDetailsText, payStatus: cheque, showPaymentGuide, showInviteCodeReferralButton } = useExtra();
    const popupRef = useRef(null);
    const modalRef = useRef(null);
    const [canShareDetails, setCanShareDetails] = useState(false);

    const isIPaid = useMemo(() => cheque?.customExtras?.i_paid === '1', [cheque?.customExtras?.i_paid]);
    // Available values are: null, 'notNow' and 'iPaid'.
    const [statusPage, setStatusPage] = useState(null);

    const statusPageMessagesMap = {
        notNow: { title: titleNotNow, body: subTitleNotNow, icon: 'zap-80' },
        iPaid: { title: titleIPaid, body: subTitleIPaid, icon: 'clock-80' },
    };

    useEffect(() => {
        logInfo(BANK_ACCOUNT_PAY_CLICKED);
        amplitude.getInstance().logEvent(BANK_ACCOUNT_PAY_CLICKED);
    }, []);

    useEffect(() => {
        setCanShareDetails(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    }, []);

    const shareDetailsAction = (title, text) => {
        navigator
            .share({ title, text })
            .then(() => logInfo('Successful share'))
            .catch((error) => logInfo('Error sharing', error));
    };

    const share = (e) => {
        e.preventDefault();
        shareDetailsAction('Title', shareDetailsText);
    };

    const renderField = (label, value, additionalInfo = '') => {
        const copyOnClick = (e) => {
            e.preventDefault();
            const result = copyTextToClipboard(value.trim());
            if (result) {
                props.toggleSnackBar(true, `${label} ${copied}`);
            }
        };

        return (
            <Row indent="24px">
                <DetailsItem
                    detailsTitle={label}
                    detailsText={value}
                    detailsButton={copyBtn}
                    detailsButtonClick={copyOnClick}
                    additionalInfo={additionalInfo}
                />
            </Row>
        );
    };

    const renderShareDetails = () => (
        <Text variant="button" color="primary" size="secondary" onClick={share}>
            {shareDetails}
        </Text>
    );

    const renderBankTransferViaSepa = () => {
        const formattedIban = getFormattedCardOrAccountNumber(status.iban, DELIVERY_BANK_ACCOUNT);
        const formattedBik = getFormattedCardOrAccountNumber(status.bik, DELIVERY_BANK_ACCOUNT);
        return (
            <>
                {status.iban && renderField(bankTransferInfoIban, formattedIban)}
                {status.bik && renderField(bankTransferInfoBankCode, formattedBik)}
            </>
        );
    };

    const renderBankTransferViaModulr = () => (
        <>
            {status.bik && renderField(bankTransferSortCode, getSortCodeWithHyphens(status.bik))}
            {status.iban && renderField(bankTransferAccountNumber, getIbanWithWhitespace(status.iban))}
        </>
    );

    const handleNotNowClick = (isI_Paid) => {
        if (isI_Paid) {
            setStatusPage('notNow');
            fetchData(`${AUTH}/payments/${cheque?.id}/iPaid?isPaid=false`, 'POST', undefined, true);
        } else {
            amplitude.getInstance().logEvent(NOT_NOW_CLICKED);
            popupRef?.current?.open(
                <PayNotNowPopup
                    toggleSpinner={props.toggleSpinner}
                    updatePageMessages={props.updatePageMessages}
                    updateMessages={props.updateMessages}
                    popupRef={popupRef}
                    modalRef={modalRef}
                    paymentId={cheque?.id}
                    handleNotNowClick={handleNotNowClick}
                />,
            );
        }
    };

    const onClickNotNow = () => {
        props.toggleModal(false);
    };

    const buttonRowOnClick = () => {
        props.toggleModal(
            true,
            <ModalComponent canCloseModal={false}>
                <HeaderPanel title={titleDisclaimer} />
                <DocsList>
                    {paymentGuideStep1Title && (
                        <DocsListItem>
                            <Text color="primary" size="main" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep1Title} />
                            </Text>
                            <Text color="secondary" size="secondary" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep1SubTitle} />
                            </Text>
                        </DocsListItem>
                    )}
                    {paymentGuideStep2Title && (
                        <DocsListItem>
                            <Text color="primary" size="main" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep2Title} />
                            </Text>
                            <Text color="secondary" size="secondary" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep2SubTitle} />
                            </Text>
                        </DocsListItem>
                    )}
                    {paymentGuideStep3Title && (
                        <DocsListItem>
                            <Text color="primary" size="main" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep3Title} />
                            </Text>
                            <Text color="secondary" size="secondary" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep3SubTitle} />
                            </Text>
                        </DocsListItem>
                    )}
                    {paymentGuideStep4Title && (
                        <DocsListItem>
                            <Text color="primary" size="main" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep4Title} />
                            </Text>
                            <Text color="secondary" size="secondary" variant="paragraph">
                                <DangerouslyHtml type="span" dangerouslyHtml={paymentGuideStep4SubTitle} />
                            </Text>
                        </DocsListItem>
                    )}
                </DocsList>
                <FixedBottomPanel isRightAligned>
                    <Button type="button" raised={true} onClick={onClickNotNow} caption={gotIt} />
                </FixedBottomPanel>
            </ModalComponent>,
            false,
        );
    };

    const handleIPaidClick = () => {
        amplitude.getInstance().logEvent(BANK_ACCOUNT_I_PAID_CLICKED);
        setStatusPage('iPaid');
        fetchData(`${AUTH}/payments/${cheque?.id}/iPaid?isPaid=true`, 'POST', undefined, true);
    };

    if (statusPage) {
        return (
            <PaymentStatus
                icon={statusPageMessagesMap[statusPage].icon}
                title={statusPageMessagesMap[statusPage].title}
                body={statusPageMessagesMap[statusPage].body}
                showInviteCodeReferralButton={showInviteCodeReferralButton}
                buttonCaption={gotIt}
            />
        );
    }

    const status = props.data.status;
    if (props.loading) {
        return <Loading />;
    } else {
        return (
            <CommonPage
                hasBottomPanel
                showChat={false}
                rightHeaderComponent={
                    <Button
                        type="link"
                        size="tight"
                        variant="tertiary"
                        onClick={() => handleNotNowClick(isIPaid)}
                        caption={bankTransferInfoCancelButton}
                        title={bankTransferInfoCancelButton}
                    />
                }
            >
                <HeaderPanel title={bankTransferTitle} subtitle={textShareDescription} />
                {showPaymentGuide && (
                    <Row indent="24px">
                        <Button type="link" variant="row" size="fit" negativeMargin caption={guideTitleLink} onClick={buttonRowOnClick} />
                    </Row>
                )}

                <Row indent="24px">
                    <DetailsItem
                        detailsTitle={bankTransferInfoAmountToSend}
                        detailsText={`${status.amount} ${status.amountCurrency}`}
                        additionalInfo={bankTransferInfoAmountToSendInfo}
                    />
                </Row>
                {status.recipientBank && renderField(bankTransferInfoRecipientBank, status.recipientBank)}
                {status.recipient && renderField(bankTransferInfoRecipient, status.recipient)}
                {status.branch && renderField(bankTransferInfoRecipientBranch, status.branch)}
                {status.name && renderField(bankTransferInfoRecipientName, status.name)}
                {status.address && renderField(bankTransferInfoRecipientAddress, status.address)}
                {status.city && renderField(bankTransferInfoRecipientCity, status.city)}
                {status.state && renderField(bankTransferInfoRecipientState, status.state)}
                {status.postalCode && renderField(bankTransferInfoRecipientPostalCode, status.postalCode)}
                {cheque?.providerFromCategory === CATEGORY_ACCOUNT_DEBIT_VIA_MODULR ? renderBankTransferViaModulr() : renderBankTransferViaSepa()}
                {getReference(status.reference, status.paymentId) &&
                    renderField(bankTransferReferenceInfo, String(getReference(status.reference, status.paymentId)), bankTransferReferenceAdditionalInfo)}
                {canShareDetails && renderShareDetails()}

                <FixedBottomPanel isRightAligned>
                    <Button type="link" raised={true} onClick={handleIPaidClick} caption={bankTransferInfoNextButton} title={bankTransferInfoNextButton} />
                </FixedBottomPanel>
                <PopupNew ref={popupRef} />
                <ModalNew ref={modalRef} />
            </CommonPage>
        );
    }
};

export default connect(null, {
    toggleSnackBar,
    toggleModal,
    updateMessages,
    updatePageMessages,
})(BasePage(BankTransferInfo));
